import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  Alert
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LabelIcon from '@mui/icons-material/Label';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ChipListEditor from './ChipListEditor';

/**
 * AlarmesSection
 * Gestiona la configuració d'alarmes i notificacions
 */
const AlarmesSection = ({
  alarmes = {},
  onChange,
  conceptesList = [],
  disabled = false
}) => {
  const isEnabled = alarmes.enabled ?? false;

  // 1. Import Càrrec
  const importCarrec = alarmes['import-carrec'] || alarmes.importCarrec || {};
  const carrecSuperiorImport = importCarrec['superior-import'] ?? importCarrec.superiorImport ?? 100.0;
  const carrecExcepcio = importCarrec.excepcio || {};
  const carrecExcepcioMovs = Array.isArray(carrecExcepcio.moviments)
    ? carrecExcepcio.moviments
    : (typeof carrecExcepcio.moviments === 'string' && carrecExcepcio.moviments.trim() ? [carrecExcepcio.moviments.trim()] : []);
  const carrecExcepcioConceptes = Array.isArray(carrecExcepcio.conceptes)
    ? carrecExcepcio.conceptes
    : (typeof carrecExcepcio.conceptes === 'string' && carrecExcepcio.conceptes.trim() ? [carrecExcepcio.conceptes.trim()] : []);

  // 2. Import Ingrés
  const importIngres = alarmes['import-ingres'] || alarmes.importIngres || {};
  const ingresSuperiorImport = importIngres['superior-import'] ?? importIngres.superiorImport ?? 100.0;
  const ingresExcepcio = importIngres.excepcio || {};
  const ingresExcepcioConceptes = Array.isArray(ingresExcepcio.conceptes)
    ? ingresExcepcio.conceptes
    : (typeof ingresExcepcio.conceptes === 'string' && ingresExcepcio.conceptes.trim() ? [ingresExcepcio.conceptes.trim()] : []);

  // 3. Tipus de Moviments
  const rawTipusMovs = alarmes['tipus-moviments'] || alarmes.tipusMoviments || [];
  const tipusMoviments = Array.isArray(rawTipusMovs)
    ? rawTipusMovs
    : (typeof rawTipusMovs === 'string' && rawTipusMovs.trim() ? [rawTipusMovs.trim()] : []);

  // 4. Falta Moviment Mensual
  const rawFaltaMovs = alarmes['falta-moviment-mensual'] || alarmes.faltaMovimentMensual || [];
  const faltaMovimentMensual = Array.isArray(rawFaltaMovs) ? rawFaltaMovs : [];

  // 5. Previsió Superada
  const previsioSuperada = alarmes['previsio-superada-concepte'] || alarmes.previsioSuperadaConcepte || {};
  const previsioEnabled = previsioSuperada.enabled ?? false;
  const previsioGap = previsioSuperada['gap-import'] ?? previsioSuperada.gapImport ?? 0;

  // Diàleg per afegir nova regla de falta moviment mensual
  const [openFaltaDialog, setOpenFaltaDialog] = useState(false);
  const [newFaltaMoviment, setNewFaltaMoviment] = useState('');
  const [newFaltaDia, setNewFaltaDia] = useState(31);
  const [newFaltaImport, setNewFaltaImport] = useState('');

  const handleGlobalToggle = (checked) => {
    onChange({
      ...alarmes,
      enabled: checked
    });
  };

  // Handlers Càrrec
  const handleCarrecImportChange = (val) => {
    const num = Math.max(0, parseFloat(val) || 0);
    onChange({
      ...alarmes,
      'import-carrec': {
        ...importCarrec,
        'superior-import': num,
        excepcio: carrecExcepcio
      }
    });
  };

  const handleCarrecExcepcioMovsChange = (newList) => {
    onChange({
      ...alarmes,
      'import-carrec': {
        ...importCarrec,
        'superior-import': carrecSuperiorImport,
        excepcio: {
          ...carrecExcepcio,
          moviments: newList
        }
      }
    });
  };

  const handleCarrecExcepcioConceptesChange = (newList) => {
    onChange({
      ...alarmes,
      'import-carrec': {
        ...importCarrec,
        'superior-import': carrecSuperiorImport,
        excepcio: {
          ...carrecExcepcio,
          conceptes: newList
        }
      }
    });
  };

  // Handlers Ingrés
  const handleIngresImportChange = (val) => {
    const num = Math.max(0, parseFloat(val) || 0);
    onChange({
      ...alarmes,
      'import-ingres': {
        ...importIngres,
        'superior-import': num,
        excepcio: ingresExcepcio
      }
    });
  };

  const handleIngresExcepcioConceptesChange = (newList) => {
    onChange({
      ...alarmes,
      'import-ingres': {
        ...importIngres,
        'superior-import': ingresSuperiorImport,
        excepcio: {
          ...ingresExcepcio,
          conceptes: newList
        }
      }
    });
  };

  // Handlers Tipus Moviments
  const handleTipusMovimentsChange = (newList) => {
    onChange({
      ...alarmes,
      'tipus-moviments': newList
    });
  };

  // Handlers Falta Moviment Mensual
  const handleAddFaltaMoviment = () => {
    if (!newFaltaMoviment.trim()) return;

    const newItem = {
      moviment: newFaltaMoviment.trim(),
      'dia-tope': parseInt(newFaltaDia, 10) || 31,
      importe: newFaltaImport ? parseFloat(newFaltaImport) : null
    };

    onChange({
      ...alarmes,
      'falta-moviment-mensual': [...faltaMovimentMensual, newItem]
    });

    setNewFaltaMoviment('');
    setNewFaltaDia(31);
    setNewFaltaImport('');
    setOpenFaltaDialog(false);
  };

  const handleDeleteFaltaMoviment = (idxToDelete) => {
    const updated = faltaMovimentMensual.filter((_, idx) => idx !== idxToDelete);
    onChange({
      ...alarmes,
      'falta-moviment-mensual': updated
    });
  };

  // Handlers Previsió Superada
  const handlePrevisioToggle = (checked) => {
    onChange({
      ...alarmes,
      'previsio-superada-concepte': {
        ...previsioSuperada,
        enabled: checked,
        'gap-import': previsioGap
      }
    });
  };

  const handlePrevisioGapChange = (val) => {
    const num = parseFloat(val) || 0;
    onChange({
      ...alarmes,
      'previsio-superada-concepte': {
        ...previsioSuperada,
        enabled: previsioEnabled,
        'gap-import': num
      }
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Capçalera i Toggle General */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActiveIcon color="primary" /> Configuració d'Alarmes i Notificacions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ajusta els llindars d'alerta per càrrecs i ingressos elevats, tipus de moviments crítics i control mensual de rebuts/nòmines.
          </Typography>
        </Box>

        <Paper variant="outlined" sx={{ px: 2, py: 0.8, borderRadius: 2, display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={(e) => handleGlobalToggle(e.target.checked)}
                disabled={disabled}
                color="success"
              />
            }
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isEnabled ? 'Alarmes Activades' : 'Alarmes Desactivades'}
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        </Paper>
      </Box>

      {!isEnabled && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Les alarmes estan globalment desactivades per a aquest perfil. No s'enviarà cap avís encara que es compleixin les condicions.
        </Alert>
      )}

      {/* Grid de Seccions d'Alarmes */}
      <Grid container spacing={3}>
        {/* ============================================================ */}
        {/* 1. Alarma Càrrec Major (import-carrec)                        */}
        {/* ============================================================ */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <CardHeader
              avatar={<TrendingDownIcon color="error" />}
              title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Alarma Càrrec Major</Typography>}
              subheader="Notifica quan es produeix una despesa superior a l'import llindar (excepte si està exclos)"
              sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
            />
            <CardContent sx={{ p: 2.5 }}>
              <TextField
                label="Import Llindar de Càrrec"
                type="number"
                size="small"
                value={carrecSuperiorImport}
                onChange={(e) => handleCarrecImportChange(e.target.value)}
                disabled={disabled || !isEnabled}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  inputProps: { min: 0, step: 10 }
                }}
                helperText="Dispara alerta si el càrrec és igual o superior a aquest valor."
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 1.5 }} />

              <ChipListEditor
                label="Excepcions per Moviments"
                helperText="Textos de moviments que no dispararan alarma de càrrec encara que superin el llindar."
                items={carrecExcepcioMovs}
                onChange={handleCarrecExcepcioMovsChange}
                placeholder="Ex. ADESLAS CENTRAL, TRASPÀS PROPI..."
                color="warning"
                disabled={disabled || !isEnabled}
              />

              <Divider sx={{ my: 1.5 }} />

              <ChipListEditor
                label="Excepcions per Conceptes"
                helperText="Conceptes assignats que no dispararan alarma de càrrec (ex. PRESTECS, INVERSIONS)."
                items={carrecExcepcioConceptes}
                onChange={handleCarrecExcepcioConceptesChange}
                placeholder="Ex. PRESTECS, ESTALVI..."
                color="primary"
                disabled={disabled || !isEnabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ============================================================ */}
        {/* 2. Alarma Ingrés Major (import-ingres)                        */}
        {/* ============================================================ */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <CardHeader
              avatar={<TrendingUpIcon color="success" />}
              title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Alarma Ingrés Major</Typography>}
              subheader="Notifica quan s'ingressa una quantitat superior a l'import llindar (excepte traspassos, etc.)"
              sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
            />
            <CardContent sx={{ p: 2.5 }}>
              <TextField
                label="Import Llindar d'Ingrés"
                type="number"
                size="small"
                value={ingresSuperiorImport}
                onChange={(e) => handleIngresImportChange(e.target.value)}
                disabled={disabled || !isEnabled}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  inputProps: { min: 0, step: 10 }
                }}
                helperText="Dispara alerta si l'ingrés és igual o superior a aquest valor."
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 1.5 }} />

              <ChipListEditor
                label="Excepcions per Conceptes"
                helperText="Conceptes que no dispararan alarma d'ingrés (ex. TRASPASSOS, TRASPASSOS PROPIS)."
                items={ingresExcepcioConceptes}
                onChange={handleIngresExcepcioConceptesChange}
                placeholder="Ex. TRASPASSOS, NÒMINA..."
                color="primary"
                disabled={disabled || !isEnabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ============================================================ */}
        {/* 3. Alarma Tipus de Moviment (tipus-moviments)                 */}
        {/* ============================================================ */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <CardHeader
              avatar={<LabelIcon color="warning" />}
              title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Alarma per Tipus de Moviment</Typography>}
              subheader="Alertar davant de qualsevol moviment associat a determinades categories o tipus crítics."
              sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
            />
            <CardContent sx={{ p: 2.5 }}>
              <ChipListEditor
                label="Tipus de Moviments Alertats"
                helperText="Etiquetes o tipus de moviment a alertar immediatament (ex. COMISIONS, INTERESSOS, DESCUBERT)."
                items={tipusMoviments}
                onChange={handleTipusMovimentsChange}
                placeholder="Ex. COMISIONS..."
                color="error"
                disabled={disabled || !isEnabled}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ============================================================ */}
        {/* 5. Previsió Superada Concepte (previsio-superada-concepte)    */}
        {/* ============================================================ */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <CardHeader
              avatar={<QueryStatsIcon color="info" />}
              title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Previsió Mensual Superada</Typography>}
              subheader="Control de desviació respecte al pressupost o previsió mensual del concepte."
              sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}
            />
            <CardContent sx={{ p: 2.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={previsioEnabled}
                    onChange={(e) => handlePrevisioToggle(e.target.checked)}
                    disabled={disabled || !isEnabled}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Habilitar Alerta de Previsió Superada
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              <TextField
                label="Marge de Tolerància (gap-import)"
                type="number"
                size="small"
                value={previsioGap}
                onChange={(e) => handlePrevisioGapChange(e.target.value)}
                disabled={disabled || !isEnabled || !previsioEnabled}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  inputProps: { step: 1 }
                }}
                helperText="Margen de tolerància abans de disparar l'avís (ex. -5 per permetre un petit marge)."
              />
            </CardContent>
          </Card>
        </Grid>

        {/* ============================================================ */}
        {/* 4. Alarma Falta Moviment Mensual (falta-moviment-mensual)     */}
        {/* ============================================================ */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <CardHeader
              avatar={<EventBusyIcon color="secondary" />}
              title={<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Falta de Moviment Mensual Esperat</Typography>}
              subheader="Dispara una alarma si abans d'un determinat dia del mes encara no s'ha registrat un moviment habitual (ex. Nòmina o ingrés de lloguer)."
              action={
                !disabled && isEnabled && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setOpenFaltaDialog(true)}
                    sx={{ mr: { xs: 0, sm: 1 }, mt: 1, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Afegir Moviment Esperat
                  </Button>
                )
              }
              sx={{
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                flexWrap: 'wrap',
                '& .MuiCardHeader-action': { m: 0, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }
              }}
            />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
              {faltaMovimentMensual.length === 0 ? (
                <Alert severity="info">No hi ha regles de moviment mensual periòdic definides.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: { xs: 450, sm: '100%' } }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Moviment</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Dia Límit</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Import (€)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Accions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {faltaMovimentMensual.map((item, idx) => {
                        const dia = item['dia-tope'] ?? item.diaTope ?? 31;
                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{item.moviment}</TableCell>
                            <TableCell>Dia {dia} de cada mes</TableCell>
                            <TableCell>
                              {item.importe != null ? `${item.importe} €` : <span style={{ color: '#999' }}>Sense fixar</span>}
                            </TableCell>
                            <TableCell align="right">
                              {!disabled && (
                                <Tooltip title="Eliminar regla">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteFaltaMoviment(idx)}
                                    disabled={!isEnabled}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Diàleg Afegir Falta Moviment Mensual */}
      <Dialog
        open={openFaltaDialog}
        onClose={() => setOpenFaltaDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Afegir Moviment Mensual Esperat</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Indica el text del moviment i el dia límit en què hauria d'aparèixer al compte:
          </Typography>
          <TextField
            label="Text o Patró del Moviment"
            placeholder="Ex. NOMINA (TRF)"
            value={newFaltaMoviment}
            onChange={(e) => setNewFaltaMoviment(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            sx={{ mb: 2 }}
          />
          <TextField
            label="Dia límit del mes (dia-tope)"
            type="number"
            value={newFaltaDia}
            onChange={(e) => {
              const v = Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1));
              setNewFaltaDia(v);
            }}
            fullWidth
            size="small"
            InputProps={{ inputProps: { min: 1, max: 31 } }}
            helperText="Valors entre 1 i 31."
            sx={{ mb: 2 }}
          />
          <TextField
            label="Import mínim o previst (€, opcional)"
            type="number"
            value={newFaltaImport}
            onChange={(e) => setNewFaltaImport(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
              inputProps: { min: 0 }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFaltaDialog(false)}>Cancel·lar</Button>
          <Button
            variant="contained"
            onClick={handleAddFaltaMoviment}
            disabled={!newFaltaMoviment.trim()}
          >
            Afegir Regla
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlarmesSection;
